import { IconButton } from '@material-ui/core';
import { Button, Grid, Icon, Tooltip } from '@material-ui/core';
import { getPaymentBrand } from 'helpers/constants';
import React, { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useRetryableObservable } from 'react-use-observable';
import { tap } from 'rxjs/operators';
import MyModal from 'v2/components/Shared/MyModal';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import QuickView from 'v2/components/Shared/QuickView';
import WhatsAppLink from 'v2/components/Shared/WhatsAppLink';
import useGetUser from 'v2/hooks/useGetUser';
import { useModal } from 'v2/hooks/useModal';
import { EContractStatus } from 'v2/interfaces/subscriptions';
import subscriptionsService from 'v2/services/subscriptions';

import type { ISubscriptions } from 'v2/interfaces/subscriptions';

import AddAffiliate from './AddAffiliate';
import ChangeChargeEmail from './ChangeChargeEmail';
import ContractDetails from './ContractDetails';
import JustifyModal from './JustifyModal';
import NegotiateModal from './NegotiateModal';
import PaymentHistory from './PaymentHistory';
import Recovery from './Recovery';
import { useStyles } from './styles';
import SubscriptionTags from '../List/SubscriptionTags';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  contract: ISubscriptions;
}

const Quickview = memo(({ isOpen, contract, onClose }: IProps) => {
  const classes = useStyles();

  const user = useGetUser();

  const [invoices, invoicesError, , retry] = useRetryableObservable(
    () => subscriptionsService.getInvoicesByContractId(contract?.id || 0),
    [contract]
  );

  const isInvoiceLoading = invoices === undefined && invoicesError === undefined;

  const [openModalCancel, setOpenModalCancel] = useModal();
  const [openModalCancelAgend, setOpenModalCancelAgend] = useModal();
  const [openNegotiateModal, setOpenNegotiateModal] = useModal();
  const [openModalSuspend, setOpenModalSuspend] = useModal();
  const [openModalReactivate, setOpenModalReactivate] = useState(false);
  const [openModalBankslip, setOpenModalBankslip] = useState(false);
  const [loadingChangePaymentBankslip, setLoadingChangePaymentBankslip] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState<ISubscriptions['payment_method']>(null);

  useEffect(() => {
    if (isOpen && contract?.payment_method) {
      setPaymentMethod(contract.payment_method);
      return;
    }

    setPaymentMethod(null);
  }, [contract, isOpen]);

  const [changePaymentTypeBankslip] = useCallbackObservable(() => {
    setLoadingChangePaymentBankslip(true);
    return subscriptionsService.changePaymentBankslip(contract.id).pipe(
      tap(
        () => {
          setPaymentMethod({ id: 1, name: 'Boleto Bancário' });
          setLoadingChangePaymentBankslip(false);
          setOpenModalBankslip(false);
          toast.success('Forma de pagamento do contrato alterada com sucesso.');
        },
        () => {
          setLoadingChangePaymentBankslip(false);
          toast.error('Ops, houve um erro ao tentar trocar a forma de pagamento.');
        }
      )
    );
  }, [contract]);

  const isProducer = useMemo(() => {
    if (!user) {
      return false;
    }

    if (!contract) {
      return false;
    }

    if (contract.product.producer_id === user.id) {
      return true;
    }

    return false;
  }, [contract, user]);

  const canSuspend = useMemo(() => {
    if (!contract) {
      return false;
    }

    if (!contract.payment_status) return false;

    const nowAllowedStatus = [EContractStatus.SUSPENSO, EContractStatus.CANCELADO];

    if (nowAllowedStatus.includes(contract.payment_status.id)) return false;

    if (!isProducer) return false;

    return true;
  }, [contract, isProducer]);

  const canCancel = useMemo(() => {
    if (!contract) {
      return false;
    }

    if (!contract.payment_status) return false;

    if (contract.payment_status.id === EContractStatus.CANCELADO) return false;

    if (!isProducer) return false;

    return true;
  }, [contract, isProducer]);

  const canReactivate = useMemo(() => {
    if (!contract) {
      return false;
    }

    if (!contract.payment_status) return false;

    if (!isProducer) return false;

    if (contract.payment_status.id === EContractStatus.SUSPENSO) return true;

    return false;
  }, [contract, isProducer]);

  const canNegotiate = useMemo(() => {
    if (!user?.negociate_invoice) {
      return false;
    }

    if (!!invoices?.length && invoices.map((i) => i.payment_status.id).includes(EContractStatus.EM_DIA)) {
      return true;
    }

    return false;
  }, [invoices, user]);

  const handlePaymentUrl = (paymentMethodName: string): string => {
    if (!paymentMethodName) {
      return '';
    }

    return getPaymentBrand(paymentMethodName || 'desconhecido').url;
  };

  const isInternational = !!(contract?.currency && contract?.currency !== 'BRL');

  return (
    <Fragment>
      <JustifyModal type='cancelar' isOpen={openModalCancel} contractId={contract?.id || 0} onClose={setOpenModalCancel} />

      <JustifyModal type='cancelar_agendado' isOpen={openModalCancelAgend} contractId={contract?.id || 0} onClose={setOpenModalCancelAgend} />

      <JustifyModal type='suspender' isOpen={openModalSuspend} contractId={contract?.id || 0} onClose={setOpenModalSuspend} />

      <JustifyModal type='reativar' isOpen={openModalReactivate} contractId={contract?.id || 0} onClose={() => setOpenModalReactivate(false)} />

      <MyModal
        title='Alterar contrato para boleto?'
        opened={openModalBankslip}
        onClose={() => setOpenModalBankslip(false)}
        onSubmit={changePaymentTypeBankslip}
        submitDisabled={loadingChangePaymentBankslip}
      >
        Confirma a alteração desse contrato para a forma de pagamento boleto?
      </MyModal>

      <NegotiateModal
        invoices={invoices}
        contractId={contract?.id || 0}
        isOpen={openNegotiateModal}
        onOk={setOpenNegotiateModal}
        onCancel={setOpenNegotiateModal}
      />

      <QuickView title={`Contrato ${contract?.id || 0}`} open={isOpen} onClose={onClose}>
        {!contract ? (
          <div />
        ) : (
          <div>
            <SubscriptionTags subscription={contract} />
            <ContractDetails contract={contract} />

            <div className='m24' />

            <div className={classes.customer}>
              {contract.customer && contract.customer.name && (
                <div>
                  <span>Nome:</span> {contract.customer.name}
                </div>
              )}
              <PermissionHide action='myeduzz:meus-contratos:ver-dados-clientes' fallback={<em>oculto</em>}>
                {contract.customer && contract.customer.email && (
                  <div>
                    <span>E-mail:</span> {contract.customer.email}
                  </div>
                )}
              </PermissionHide>
              <PermissionHide action='myeduzz:meus-contratos:ver-dados-clientes' fallback={<em>oculto</em>}>
                {contract.customer && contract.customer.celphone && (
                  <div>
                    <span>Fone:</span> <WhatsAppLink phone={contract.customer.celphone} />
                  </div>
                )}
              </PermissionHide>

              {paymentMethod?.id && (
                <div>
                  <span>Forma de pgto:</span>
                  {!loadingChangePaymentBankslip && (
                    <img className={`my-table__payment ${classes.spaceRight}`} src={handlePaymentUrl(paymentMethod.name)} alt='Card' />
                  )}
                  {paymentMethod.name}
                  {contract?.card_number && paymentMethod.id !== 1 ? ' - ' + contract.card_number + ' ' : ''}
                  {paymentMethod.id !== 1 && !isInternational ? (
                    <PermissionHide action='myeduzz:meus-contratos:editar'>
                      <Tooltip placement='top' title='Alterar para boleto'>
                        <IconButton onClick={() => setOpenModalBankslip(true)} style={{ width: 40, height: 40 }}>
                          <Icon style={{ fontSize: '1rem', color: '#596375' }}>edit</Icon>
                        </IconButton>
                      </Tooltip>
                    </PermissionHide>
                  ) : (
                    ''
                  )}
                </div>
              )}

              <ChangeChargeEmail contract={contract} />

              <div>
                <span>Tipo de frequência:</span> {contract?.frequency_details?.frequency_type || '-'}
              </div>

              <div>
                <span>Frequência:</span> {contract?.frequency_details?.frequency || '-'}
              </div>

              <div>
                <span>Limite de cobranças:</span> {contract?.frequency_details?.number_of_charges || '-'}
              </div>
            </div>

            <div className='m24' />

            <PaymentHistory contractId={contract?.id || 0} retryInvoices={retry} invoices={invoices} isLoading={isInvoiceLoading} />

            <div className='m8' />

            <Recovery isLoading={isInvoiceLoading} invoices={invoices} contract={contract} />

            <div className='m8' />

            {contract?.payment_status?.id !== EContractStatus.CANCELADO && <AddAffiliate contract={contract} />}

            <Grid container spacing={1}>
              {canSuspend && (
                <PermissionHide action='myeduzz:meus-contratos:suspender-contrato'>
                  <Grid item md={4}>
                    <Button color='default' variant='outlined' onClick={setOpenModalSuspend} disabled={false}>
                      Suspender
                    </Button>
                  </Grid>
                </PermissionHide>
              )}

              {canReactivate && (
                <Fragment>
                  <PermissionHide action='myeduzz:meus-contratos:reativar-contrato'>
                    <Grid item md={4}>
                      <Button color='default' variant='outlined' onClick={() => setOpenModalReactivate(true)} disabled={false}>
                        Reativar
                      </Button>
                    </Grid>
                  </PermissionHide>
                </Fragment>
              )}

              {canCancel && (
                <Fragment>
                  <PermissionHide action='myeduzz:meus-contratos:cancelar-contrato'>
                    <Grid item md={4}>
                      <Button color='default' variant='outlined' onClick={setOpenModalCancel} disabled={false}>
                        Cancelar
                      </Button>
                    </Grid>
                  </PermissionHide>
                </Fragment>
              )}

              {canNegotiate && (
                <PermissionHide action='myeduzz:meus-contratos:negociar-faturas'>
                  <Grid item md={12}>
                    <Button color='default' variant='outlined' onClick={setOpenNegotiateModal}>
                      Negociar
                    </Button>
                  </Grid>
                </PermissionHide>
              )}
            </Grid>
          </div>
        )}
      </QuickView>
    </Fragment>
  );
});

export default Quickview;
